import {
  InventoryAuthority,
  ImageItemFields,
  CaptureMutation,
} from '@qkindex/contracts';
import { Item } from '../../domain/item';

export type CaptureDraftFields = ImageItemFields;

export interface ItemEditOptions {
  mergeKeywords?: boolean;
  reviewedRevision?: number;
  /** Moves must observe the saved source before starting their separate operation. */
  awaitConfirmation?: boolean;
}
export type ItemEditEnqueue = (
  item: Item,
  fields: Partial<ImageItemFields>,
  image?: Blob | null,
  options?: ItemEditOptions,
) => Promise<unknown>;
export interface CaptureEditSnapshot {
  operationId: string;
  localRevision: number;
  expectedRevision: number;
  fields: Partial<CaptureDraftFields>;
  mergeKeywords: boolean;
  image?: Blob | null;
  imageHash?: string;
  uploadAttempted?: boolean;
  uploaded?: boolean;
  uploadPath?: string;
  serverAccepted?: boolean;
}

export type RapidCaptureState =
  | 'pending'
  | 'submitting'
  | 'analysis-submitting'
  | 'confirming'
  | 'failed'
  | 'terminal';

export type RapidCaptureAuthority = InventoryAuthority & {
  membershipVersion: number;
  lifecycleVersion?: number;
};

export interface RapidCaptureRecord {
  /** Updates use the same immutable begin/upload/commit protocol as additions. */
  mutation?: CaptureMutation;
  mutationFields?: Partial<ImageItemFields>;
  publishedStoragePath?: string;
  /** A replacement/removal saved after the immutable initial command. */
  pendingImage?: Blob | null;
  pendingImageRevision?: number;
  publishedImage?: Blob | null;
  publishedImageOperationId?: string;
  publishedImageRevision?: number;
  serverAccepted?: boolean;
  serverRevision?: number;
  controlAction?: 'retry' | 'save' | 'analyze';
  aiStarted?: boolean;
  aiDismissed?: boolean;
  aiSettlement?: 'refunded' | 'reconciling';
  /** Older records are retained for explicit recovery, never automatically replayed. */
  schemaVersion: 5 | 6;
  imageHash?: string;
  uploadPath?: string;
  uploaded?: boolean;
  uploadAttempted?: boolean;
  localRevision?: number;
  syncedRevision?: number;
  edits?: Partial<CaptureDraftFields>;
  editFieldRevisions?: Partial<Record<keyof CaptureDraftFields, number>>;
  editSnapshot?: CaptureEditSnapshot;
  /** True while the description is the user's input, before seeing the AI merge. */
  mergeKeywords?: boolean;
  itemRevision?: number;
  itemUnavailable?: boolean;
  publishedFields?: CaptureDraftFields;
  publishedRevision?: number;
  leaseOwner?: string;
  leaseExpiresAt?: number;
  editorLeases?: Record<string, number>;
  operationId: string;
  userId: string;
  collectionId: string;
  membershipVersion: number;
  lifecycleVersion?: number;
  compatibilityMode: RapidCaptureAuthority['compatibilityMode'];
  itemId: string;
  image: Blob;
  analyzeImage?: boolean;
  keywords: string;
  location: string;
  locationId?: string;
  qty: number;
  tagIds?: string[];
  state: RapidCaptureState;
  itemCreated: boolean;
  itemCreateAttempted?: boolean;
  attempts: number;
  editAttempts?: number;
  controlAttempts?: number;
  createdAt: string;
  updatedAt: string;
  nextAttemptAt?: string;
  analysisOperationId?: string;
  acceptedAiCostVersion?: string;
  pendingAiCostVersion?: string;
  pendingAiCredits?: number;
  completion?: 'item' | 'analysis-complete';
  terminalReason?:
    | 'revoked'
    | 'membership-changed'
    | 'collection-deleting'
    | 'authority-invalid'
    | 'client-unsupported';
  aiSkipReason?: 'cost-unconfirmed' | 'cost-changed';
  lastError?: {
    phase?: 'upload' | 'ai' | 'edits';
    category:
      | 'network'
      | 'authentication'
      | 'capacity'
      | 'quota'
      | 'permission'
      | 'conflict'
      | 'unknown';
    message: string;
    retryAfterSeconds?: number;
  };
}

export interface RapidCaptureDraft {
  image: Blob;
  analyzeImage?: boolean;
  keywords: string;
  location: string;
  locationId?: string;
  qty: number;
  tagIds?: string[];
}

export interface RapidCaptureStats {
  pending: number;
  active: number;
  analysisProcessing: number;
  confirming: number;
  failed: number;
  total: number;
  bytes: number;
}

export const sanitizeRapidCaptureTerminalRecord = (
  record: RapidCaptureRecord,
  terminalReason: NonNullable<RapidCaptureRecord['terminalReason']>,
  lastError: NonNullable<RapidCaptureRecord['lastError']>,
): RapidCaptureRecord => {
  // Unsupported local schemas may still contain a companion image. Access
  // invalidation must erase that private blob as well as the primary photo.
  const sanitized = { ...record } as RapidCaptureRecord & {
    labelScanImage?: Blob;
    ocrOperationId?: string;
    analysisResult?: unknown;
    analysisSourcePath?: string;
    skipSavedAnalysis?: boolean;
  };
  delete sanitized.labelScanImage;
  delete sanitized.ocrOperationId;
  delete sanitized.analysisResult;
  delete sanitized.analysisSourcePath;
  delete sanitized.skipSavedAnalysis;
  return {
    ...sanitized,
    state: 'terminal',
    controlAction: undefined,
    terminalReason,
    image: new Blob([], { type: 'application/octet-stream' }),
    imageHash: undefined,
    uploadPath: undefined,
    edits: undefined,
    editFieldRevisions: undefined,
    editSnapshot: undefined,
    pendingImage: undefined,
    publishedImage: undefined,
    mutationFields: undefined,
    publishedStoragePath: undefined,
    publishedFields: undefined,
    editorLeases: undefined,
    leaseOwner: undefined,
    leaseExpiresAt: undefined,
    analyzeImage: undefined,
    aiSettlement: undefined,
    analysisOperationId: undefined,
    keywords: '',
    tagIds: undefined,
    location: '',
    locationId: undefined,
    qty: 1,
    acceptedAiCostVersion: undefined,
    pendingAiCostVersion: undefined,
    pendingAiCredits: undefined,
    aiSkipReason: undefined,
    nextAttemptAt: undefined,
    lastError,
    updatedAt: new Date().toISOString(),
  };
};

/** View-only composition. Initial submission fields must never be mutated by edits. */
export const captureDraftFields = (
  record: RapidCaptureRecord,
  published?: Item,
): CaptureDraftFields => {
  const latest =
    record.publishedFields &&
    (record.publishedRevision ?? -1) > (published?.revision ?? -1)
      ? record.publishedFields
      : published;
  return {
    keywords:
      latest?.keywords ?? record.publishedFields?.keywords ?? record.keywords,
    location:
      latest?.location ?? record.publishedFields?.location ?? record.location,
    locationId:
      latest?.locationId ??
      record.publishedFields?.locationId ??
      record.locationId,
    tagIds: latest?.tagIds ?? record.publishedFields?.tagIds ?? record.tagIds,
    qty: latest?.qty ?? record.publishedFields?.qty ?? record.qty,
    ...(!record.itemCreated ? record.mutationFields : {}),
    ...record.edits,
  };
};

/** A local photo revision keeps its URL across upload and server acknowledgement. */
export function capturePhoto(record: RapidCaptureRecord):
  | {
      image: Blob | null;
      key: string;
      operationId?: string;
      committed: boolean;
    }
  | undefined {
  if (record.pendingImage !== undefined)
    return {
      image: record.pendingImage,
      key: `${record.operationId}:${record.pendingImageRevision}`,
      committed: false,
    };
  if (record.publishedImage !== undefined)
    return {
      image: record.publishedImage,
      key: `${record.operationId}:${record.publishedImageRevision}`,
      operationId: record.publishedImageOperationId,
      committed: true,
    };
  if (record.mutation?.imageAction === 'keep') return undefined;
  return {
    image: record.mutation?.imageAction === 'remove' ? null : record.image,
    key: record.operationId,
    operationId: record.operationId,
    committed: record.itemCreated,
  };
}

export const hasCaptureEdits = (record: RapidCaptureRecord): boolean =>
  Boolean(record.editSnapshot) ||
  record.pendingImage !== undefined ||
  (record.localRevision ?? 0) > (record.syncedRevision ?? 0);

export const hasPendingCaptureAI = (record: RapidCaptureRecord): boolean =>
  record.analyzeImage === true &&
  !record.aiDismissed &&
  record.completion !== 'analysis-complete';

/** A completion receipt is not proof the user has seen the merged text yet. */
export const captureDraftMergeKeywords = (
  record: RapidCaptureRecord,
  published?: Item,
): boolean => {
  if (record.mergeKeywords === false) return false;
  if (
    record.edits?.keywords !== undefined ||
    record.editSnapshot?.fields.keywords !== undefined
  )
    return true;
  const visibleRevision = Math.max(
    published?.revision ?? -1,
    record.publishedRevision ?? -1,
  );
  return !(
    record.completion === 'analysis-complete' &&
    visibleRevision >= (record.itemRevision ?? 0)
  );
};
